import _ from 'lodash';
import moment from 'moment';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { clearInterval, setInterval } from 'worker-timers';
import {
  fetchCheckExpiredToken,
  handleMonitoringFrontendError,
  fetchQuerySignature,
  clearApolloCache,
} from '@/shared/api';
import { fetchHolterBeatEventsCount } from '@/entities/beat';
import { fetchHolterEcgDataMap, clearCacheAPI, CONFIG_ECG } from '@/entities/ecg';
import { fetchHolterRhythmEventsCount } from '@/entities/event';
import { fetchHolterTasks, handleUpdateHolterProfile } from '@/entities/study';
import { fetchMctDailySummaries } from '@/entities/heart-rate';
import { fetchListEcgBookmarks } from '@/entities/bookmark';
import {
  fetchMigrateAiData,
  fetchNewReportData,
  handleLogAiBugFinalBeat,
  handleMarkReportAsArtifactReport,
  handleMigrateAiBeatFinalPaths,
  useReportStore,
  type NewReportDataResult,
} from '@/entities/report';
import { useUserStore } from '@/entities/user';
import { ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  FRONT_END_ERRORS,
  LOADING_TYPES,
  URL_CALLCENTER_PORTAL,
  AI_PHASE,
  TAB_KEY,
} from '@/shared/config';
import { IReportInfo } from '@/shared/model';
import { StripsManagementV2Handle } from '@/widgets/strips-management';
import { logError } from '@/shared/lib/apiHelpers';
import {
  clearDBBeatData,
  getBeatDataCount,
  resetHistoryState,
  checkStudyMetadata,
  deleteUnusedDatabases,
  reconcileMetadata,
  getStudyMetadata,
  updateStatusAndMetadata,
  syncBeatInfo,
  clearAllDataState,
  getDataState,
  upsertDataState,
  enableReportPersistence,
  disableReportPersistence,
  timezoneToOffset,
  auth,
  deleteUnusedIndexDB,
  deleteNonEssentialDatabases,
  registerTab,
  unregisterTab,
  TAB_HEARTBEAT_MS,
} from '@/shared/lib';
import {
  convertBytesToGB,
  generateAiStatusReport,
  generateListDate,
  isHolterProcessingAvailable,
} from '@/shared/lib/aiUtils';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import socketio from '@/shared/lib/socketio';
import { useLoadingStore } from '@/shared/lib/loadingStore';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  downloadOnlyBeatByDateEpoch,
  groupDataByEpochStartOfDay,
  handleDownloadBeatList,
  terminateThread,
} from './worker/workerHandler';
import { checkHasChangedSavedIndexDB } from './handler';

/*
  All hot keys:
  N,S,V,Q,Z,X,C,W,F,ESC,B,CTRL+A,CTRL+S,D,R,A,arrow up down left right,>
  N,S,V,Q: change beat type
  D: delete beat/event
  Z: previous page
  X: next page
  C: open change duration event
  W: open add new event
  F: open add bookmark
  R: mark/unmark event reviewed
  ESC: escape from mode
  B: toggle Add Remove Beat mode (ECGViewer)
  A: mark event as artifact
  CTRL+A: select all strip
  CTRL+S: Save data
  arrow up down left right: navigate through strip in page
  1,2,3,4,5,6: set bulk action beat
*/

/**
 * Drives the AI report page - report and ECG data loading, IndexedDB caching, worker downloads, tab navigation and the page's modals. Fetches report data, event counts and bookmarks into the report store, downloads beat and ECG files through worker threads after checking storage quota and cache completeness, guards tab switches against unsaved edits, polls token expiry and inactivity, and returns that state with its handlers and tab refs.
 *
 * @returns An object carrying the page state, report data, tab refs and every handler the AI report page renders with.
 */
const useAiReportPage = () => {
  const params = useParams<{ studyFid: string; profileId: string }>();
  const { studyFid, profileId } = params;
  const meRedux = useUserStore((s) => s.me);
  const loading = useLoadingStore((s) => s.loading);

  const loadingPageAction = useLoadingStore((s) => s.loadingPage);

  // Check if is mac or not
  const isMacOS = navigator.userAgent.indexOf('Mac') !== -1;

  const defaultActiveTab = useRef<string>(TAB_KEY.PDF_REPORT);

  const [state, setState] = useMergeState({
    isLoadedData: false,
    isGeneratingNormalReportInCallCenter: false,
    isOpenStudyInfoDrawer: false,
    isOpenPatientInfoDrawer: false,
    isOpenFacilityInfoDrawer: false,
    isOpenNoActivityModal: false,
    isOpenConfirmArtifact: false,
    isOpenConfirmNavigateTab: false,
    isOpenUseDbIndexModal: false,
    isLoadingSaveAiReport: false,
    isOpenWarningLimitStorage: false,
    conflictUsers: [],
    activeTab: defaultActiveTab.current,
    isOpenNoApiCallingModal: false,
    isExpiredToken: false,
    isShowConfirmMigrate403: false,
    pendingProfiles: [],
    isOpenIndexDBError: false,
  });

  const [isNotEdit, setIsNotEdit] = useState(true);
  const [isLoadIndexDB, setIsLoadIndexDB] = useState(false);
  const [isLoadBeatData, setIsLoadBeatData] = useState(true);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [isResetingStudy, setIsResetingStudy] = useState(false);
  const [isOpenWrongSomething, setIsOpenWrongSomeThing] = useState(false);
  // typeWrong: 0: no problem, 1: reload, 2: contact devSupport
  const [typeWrong, setTypeWrong] = useState(0);
  const reportData = useReportStore((s) => s.reportData);
  const setReportData = useReportStore((s) => s.actions.setReportData);
  const reportInfo = useReportStore((s) => s.reportInfo);
  const setReportInfo = useReportStore((s) => s.actions.setReportInfo);
  const [aiProcessing, setAiProcessing] = useState(true);
  /**
   * Reads the current report info snapshot straight off the report store. Calls getState rather than subscribing, so callers see the latest value without triggering a re-render.
   *
   * @returns The current reportInfo value held by the report store.
   */
  const getReportInfo = () => useReportStore.getState().reportInfo;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const setEcgDataMap = useReportStore((s) => s.actions.setEcgDataMap);
  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const setIsStartedAiProcessing = useReportStore((s) => s.actions.setIsStartedAiProcessing);
  const isOpenProcessingLimit = useReportStore((s) => s.isOpenProcessingLimit);
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const limitProcessingStudy = useReportStore((s) => s.limitProcessingStudy);
  const setLimitProcessingStudyState = useReportStore((s) => s.actions.setLimitProcessingStudy);
  const setOriginalDailySummaries = useReportStore((s) => s.actions.setOriginalDailySummaries);
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  const setFindings = useReportStore((s) => s.actions.setFindings);

  useEffect(() => {
    setIsWsConnected(socketio.isConnected);
  }, []);
  const setFacilityNote = useReportStore((s) => s.actions.setFacilityNote);
  const setProfileAfibAvgHr = useReportStore((s) => s.actions.setProfileAfibAvgHr);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const setCombinedEventsInHeatmap = useReportStore((s) => s.actions.setCombinedEventsInHeatmap);
  const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
  const setActionSnapshot = useReportStore((s) => s.actions.setActionSnapshot);
  const setHolterBeatEventsCount = useReportStore((s) => s.actions.setBeatEventCount);
  const setHolterRhythmEventsCount = useReportStore((s) => s.actions.setRhythmEventCount);
  const setIsRenderViewerEcg = useReportStore((s) => s.actions.setIsRenderViewerEcg);
  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const isUndoDisabled = useReportStore((s) => s.isUndoDisabled);
  const setIsUndoDisabled = useReportStore((s) => s.actions.setIsUndoDisabled);
  const isRedoDisabled = useReportStore((s) => s.isRedoDisabled);
  const setIsRedoDisabled = useReportStore((s) => s.actions.setIsRedoDisabled);
  const isDisabledBtn = useReportStore((s) => s.isDisabledBtn);
  const setIsDisabledBtn = useReportStore((s) => s.actions.setIsDisabledBtn);
  const [checkInitialUserRoom, setCheckInitialUserRoom] = useState(null);
  const listDownloadFile = useReportStore((s) => s.listDownloadFile);
  const setListDownloadFile = useReportStore((s) => s.actions.setListDownloadFile);
  const setIsDownloadingBeatFile = useReportStore((s) => s.actions.setIsDownloadingBeatFile);
  const isDownloadingPdfReport = useReportStore((s) => s.isDownloadingPdfReport);
  const setInitTemplateOverview = useReportStore((s) => s.actions.setInitTemplateOverview);

  // State for Migrate AI data
  const [migrateAiData, setMigrateAiData] = useState({
    isMigrating: false,
    status: 'Processing',
    percentage: 0,
    estimateTime: 0, // seconds
    note: '',
  });

  const checkInitialUserRoomRef = useRef(true);
  const isInitialMount = useRef(true);
  const isInitialSessionInfo = useRef(true);
  const startTime = useRef(0);
  const endTime = useRef(0);
  const checkExpiredTokenInterval = useRef(null);

  // Check if report is currently being generated
  const isGeneratingReport =
    reportData?.report?.status === 'Rendering' ||
    loading.loadingText === LOADING_TYPES.GENERATE_REPORT;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsUndoDisabled(true);
      setIsRedoDisabled(true);
    }, 20);

    /**
     * Syncs the undo and redo buttons with the history cursor broadcast by the emitter. Disables undo below the first entry and redo at or past the last one.
     *
     * @param index - Current history position and its maximum, as currentIndex and maxIndex.
     * @returns Nothing.
     */
    const updateIndex = (index: { currentIndex: number; maxIndex: number }) => {
      setIsUndoDisabled(index.currentIndex < 1);
      setIsRedoDisabled(index.currentIndex >= index.maxIndex);
    };

    const undoRedoCurrentIndexListener = emitter.addListener(
      EMITTER_CONSTANTS.UNDO_REDO_CURRENT_INDEX,
      updateIndex,
    );

    // Clean up the event listener on component unmount
    return () => {
      clearTimeout(timeout); // Clear the timeout if the component unmounts before the timeout finishes
      undoRedoCurrentIndexListener.remove();
    };
  }, []);

  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyId, facilityId, reportId, isAbortedStudy, startReport, timezoneOffset } =
    reportInfo as IReportInfo;

  const roomReport = useRef<any>();
  const updatingTabCount = useRef(0);
  const tabIndexConfirmNavigate = useRef<any>();
  const beatsRef = useRef<any>(); // Beats
  const ecgEventsRef = useRef<any>(); // ECG Events
  const stripsRef = useRef<StripsManagementV2Handle>(null); // Strips Management
  const pdfReportRef = useRef<any>(); // Report
  const hrSummaryRef = useRef<any>(); // HRSummary
  const findingRef = useRef<any>();
  const ecgDisclosureRef = useRef<any>();
  const sessionInfoRef = useRef<any>();
  const timerRenderReport = useRef<any>();
  const indexedDbInfoRef = useRef<any>();
  const checkBeatsTimeOut = useRef<any>();
  const isPreventChangeTabWhenSaving = useRef(false);

  const BEAT_RETRY_LIMIT = 2;
  const BEAT_RETRY_DELAY = 800;

  /**
   * Loads the holter beat and rhythm event counts for the open study and profile. Runs both count queries in parallel and pushes each result into the report store, logging a failure instead of throwing.
   *
   * @returns A promise resolving once both counts are in the store.
   */
  const fetchEventsCount = async () => {
    try {
      const eventsCountFilter = {
        studyId,
        profileId,
      };
      const promises = [
        fetchHolterBeatEventsCount(eventsCountFilter, false, null, false),
        fetchHolterRhythmEventsCount(eventsCountFilter, false, null, false),
      ];
      const [holterBeatEventsCount, holterRhythmEventsCount] = await Promise.all(promises);
      setHolterBeatEventsCount(holterBeatEventsCount);
      setHolterRhythmEventsCount(holterRhythmEventsCount);
    } catch (error) {
      logError('Failed to fetch holter rhythm events count: ', error);
    }
  };

  /**
   * Restores the report tab that was active in the previously persisted session. Reads the activeTab record out of the persisted state and, when it holds a value, writes it into page state and into the default-tab ref.
   *
   * @returns A promise resolving once the persisted tab has been applied.
   */
  const getDatabaseState = async () => {
    const activeTab = await getDataState('activeTab');
    if (activeTab?.value) {
      setState({ activeTab: activeTab?.value });
      defaultActiveTab.current = activeTab?.value;
    }
  };

  /**
   * Wipes the editing state persisted in IndexedDB and writes back only what the current session still needs. Clears all stored state, restores the findings from the ref, and re-persists the active tab plus, while unsaved edits exist, the session info.
   *
   * @returns A promise resolving once the stored state has been cleared and rewritten.
   */
  const clearDataAndResaveSessionInfo = async () => {
    await clearAllDataState();
    setFindings(findingRef.current);
    if (!isNotEdit && sessionInfoRef.current) {
      upsertDataState('sessionInfo', sessionInfoRef.current).catch(() => {});
      upsertDataState('activeTab', state.activeTab).catch(() => {});
    } else {
      upsertDataState('activeTab', state.activeTab).catch(() => {});
    }
  };

  /**
   * Decides what to do with the editing state left in IndexedDB by a previous session. Clears and re-saves when the stored session belongs to another study, profile or session time or is more than three days old; otherwise compares the saved data against the current findings and disclosure and either opens the reuse prompt, silently keeps the data while an AI save is in flight, or clears it, then prunes unused databases.
   *
   * @param info - Study id, profile id and session time of the session now being opened.
   * @returns A promise resolving once the stored session has been reused, cleared or prompted for.
   */
  const handleClearIndexDB = async (info: {
    studyId: string;
    profileId: string;
    sessionTime: any;
  }) => {
    try {
      const dataSave = await getDataState('sessionInfo');
      if (dataSave?.value && !isNotEdit) {
        const { studyId, profileId, sessionTime } = info;
        // The session-time effect rewrites the persisted sessionInfo with a fresh time on every
        // load and can win the race against this check, so a plain equality with the server's
        // (older) holterProfile.sessionTime would always mismatch and wipe the edits. Treat the
        // persisted session as continuous when its time matches the server session OR this load's
        // own fresh write (sessionInfoRef); only a real study/profile change or truly stale
        // (>3 day) data clears the saved edits.
        const savedSessionTime = moment(dataSave?.value?.sessionTime).valueOf();
        const isOwnFreshSession =
          !!sessionInfoRef.current &&
          savedSessionTime === moment(sessionInfoRef.current.sessionTime).valueOf();
        const sessionMatches =
          isOwnFreshSession || savedSessionTime === moment(sessionTime).valueOf();
        if (
          dataSave?.value?.studyId !== studyId ||
          dataSave?.value?.profileId !== profileId ||
          !sessionMatches ||
          (!isOwnFreshSession && moment().diff(moment(dataSave?.value?.sessionTime), 'day') > 3)
        ) {
          // clean all data
          clearDataAndResaveSessionInfo();
          setIsLoadIndexDB(true);
        } else {
          // show popup when stay in tab 1 or tab 2
          const activeTab = await getDataState('activeTab');

          // check has change data
          // pre-existing bug: currentFinding isn't a param of checkHasChangedSavedIndexDB (handler.ts) - silently ignored.
          const isChanged = await checkHasChangedSavedIndexDB({
            activeTab: activeTab?.value,
            currentFinding: findingRef.current,
            currentEcgDisclosure: ecgDisclosureRef.current,
          } as any);

          if (isChanged) {
            const isAISaving = JSON.parse(localStorage.getItem(`ai_saving-${profileId}`));
            if (!isAISaving) {
              setState({ isOpenUseDbIndexModal: true });
            } else {
              setFindings(findingRef.current);
              if (!isNotEdit && sessionInfoRef.current) {
                upsertDataState('sessionInfo', sessionInfoRef.current).catch(() => {});
                upsertDataState('activeTab', state.activeTab).catch(() => {});
              }
              setIsLoadIndexDB(true);
            }
          } else {
            clearDataAndResaveSessionInfo();
            setIsLoadIndexDB(true);
          }
        }
      } else {
        clearDataAndResaveSessionInfo();
        setIsLoadIndexDB(true);
      }
      deleteUnusedIndexDB();
      deleteNonEssentialDatabases();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Throws away the edits found in IndexedDB and starts the session clean. Closes the reuse prompt, clears and re-saves the session state, and flips isLoadIndexDB.
   *
   * @returns Nothing.
   */
  const onDiscardChangeSavedData = () => {
    setState({ isOpenUseDbIndexModal: false });
    clearDataAndResaveSessionInfo();
    setIsLoadIndexDB(true);
  };

  /**
   * Keeps the edits previously persisted to IndexedDB and resumes the session with them. Closes the saved-data prompt and flips isLoadIndexDB so the stored state is loaded.
   *
   * @returns Nothing.
   */
  const onKeepEditingWithSavedData = () => {
    setState({ isOpenUseDbIndexModal: false });
    setIsLoadIndexDB(true);
  };

  /**
   * Clears the undo/redo history of the report page. Resets the persisted history state and disables both the undo and the redo button.
   *
   * @returns Nothing.
   */
  const resetState = () => {
    resetHistoryState();
    setIsUndoDisabled(true);
    setIsRedoDisabled(true);
  };

  /**
   * Refreshes the persisted study metadata with the current beat file locations. Flattens the data-map entries into itemId, start, stop and beatFinalPath records and hands them to updateStatusAndMetadata as dataFinalPath.
   *
   * @param ecgDataMap - ECG data map whose entries carry the beat final paths to persist.
   * @returns Nothing.
   */
  const updateDataEcgDataInMetadata = (ecgDataMap: any) => {
    const dataFinalPath: any[] = [];
    ecgDataMap.data.forEach((item: any) => {
      const data = {
        itemId: item.id,
        start: item.start,
        stop: item.stop,
        beatFinalPath: item.beatFinalPath,
      };
      dataFinalPath.push(data);
    });
    updateStatusAndMetadata({ dataFinalPath });
  };

  // worker update beat data
  /**
   * Re-downloads a subset of beat files in a background worker after the server updated them. Spawns an update-mode beat thread, downloads the entries grouped by week with the map's query signature, and always terminates the thread a second later.
   *
   * @param ecgDataMap - Trimmed ECG data map holding only the entries to re-download.
   * @returns A promise resolving once the beats have been re-downloaded.
   */
  const updateAndProcessBeatsInWorker = async (ecgDataMap: any, fullMap: any = ecgDataMap) => {
    let threadBeat: any;
    try {
      threadBeat = await handleDownloadBeatList(ecgDataMap, true, fullMap);
      if (threadBeat) {
        const groupedData = groupDataByEpochStartOfDay(ecgDataMap.data, 'weeks', 'start');
        await downloadOnlyBeatByDateEpoch(threadBeat, groupedData, ecgDataMap?.querySignature);
      }
    } catch (error) {
      console.error('Error updateAndProcessBeatsInWorker:', error);
    } finally {
      if (threadBeat) {
        setTimeout(() => {
          terminateThread(threadBeat);
        }, 1000);
      }
    }
  };

  /**
   * Merges updated beat final paths from a realtime message into a copy of the ECG data map. When the message carries summaries and the map holds data, assigns each summary's beatFinalPath onto the matching entry, re-downloads only the touched entries in a worker and persists the new paths to the study metadata.
   *
   * @param msg - Realtime payload whose summaries carry the new beat final paths.
   * @param ecgDataMap - ECG data map that is shallow-copied and updated.
   * @returns The updated copy of the ECG data map.
   */
  const handleUpdateBeats = (msg: any, ecgDataMap: any) => {
    const prevEcgDataMap = ecgDataMap;
    const cloneEcgDataMap = { ...prevEcgDataMap };
    if (
      (msg.summaries?.length || msg.beatsUpdated?.summaries?.length) &&
      !_.isEmpty(prevEcgDataMap?.data)
    ) {
      _.forEach(msg.summaries || msg.beatsUpdated?.summaries, (summary) => {
        const foundData = _.find(cloneEcgDataMap.data, (x) => x.id === summary.id);
        if (foundData) {
          _.assign(foundData, {
            beatFinalPath: summary?.beatFinalPath,
          });
        }
      });
      const summaryIds = _.map(msg.summaries || msg.beatsUpdated?.summaries, 'id');
      const updateEcgDataMap = {
        ...cloneEcgDataMap,
        data: _.filter(cloneEcgDataMap.data, (item) => summaryIds.includes(item.id)),
      };
      updateAndProcessBeatsInWorker(updateEcgDataMap, cloneEcgDataMap);
      updateDataEcgDataInMetadata(cloneEcgDataMap);
    }
    return cloneEcgDataMap;
  };

  /**
   * Applies a beats-updated realtime message to the cached ECG data map. Rebuilds the map through handleUpdateBeats, which rewrites the beat final paths and re-downloads the touched files, then stores the result unless it is deeply equal to the previous one.
   *
   * @param msg - Realtime payload carrying the updated hourly summaries.
   * @param ecgDataMap - Current ECG data map the rebuild starts from.
   * @returns A promise resolving once the updated data map has been stored.
   */
  const handleReloadBeat = async (msg: any, ecgDataMap: any) => {
    try {
      const cloneEcgDataMap = handleUpdateBeats(msg, ecgDataMap);
      setEcgDataMap((prev: any) => {
        if (_.isEqual(prev, cloneEcgDataMap)) {
          return prev;
        }
        return cloneEcgDataMap;
      });
    } catch (error) {
      logError('Failed to reload beat', error);
    }
  };

  useEmitter(
    EMITTER_CONSTANTS.BEATSUPDATED_EVENT,
    async (msg: any) => {
      if (msg.phase === AI_PHASE.HOURLY_SUMMARY) {
        await handleReloadBeat(msg, ecgDataMap);
      }

      if (state.activeTab !== TAB_KEY.BEATS) {
        setInitTemplateOverview([]);
        emitter.emit(EMITTER_CONSTANTS.RELOAD_TEMPLATE_OVERVIEW);
      }
    },
    [state.activeTab, ecgDataMap],
  );

  /**
   * Reloads the MCT daily summaries after the server signals they changed. Fetches them for the study friendly id and profile, replaces the original summaries in the store and empties the updated-summaries list.
   *
   * @returns A promise resolving once the summaries have been refreshed.
   */
  const handleReloadSummary = async () => {
    try {
      const input = {
        studyFid: studyFid ? parseInt(studyFid, 10) : undefined,
        profileId,
      };
      const mctDailySummaries = await fetchMctDailySummaries(input, null, false, false);
      if (mctDailySummaries) {
        setOriginalDailySummaries(mctDailySummaries);
        setUpdatedDailySummaries([]);
      }
    } catch (error) {
      console.error('Error handling reload event:', error);
    }
  };

  useEmitter(EMITTER_CONSTANTS.DAILY_SUMMARY_UPDATED, handleReloadSummary);

  /**
   * Loads everything the report page needs for a study, or crashes the page when the study is not reportable. Pulls the report data, then the ECG data map, holter tasks and study reports in parallel; applies sampling frequency, gain and channels to the ECG config, builds the report info and empty heatmap buckets, sorts and converts data-map timestamps to moments, stores summaries and the facility note, and clears the page loader.
   *
   * @param studyFid - Study friendly id from the route, parsed to a number for the query.
   * @returns A promise resolving once the store is populated and the page loader cleared.
   */
  const fetchReportData = async (studyFid: string): Promise<boolean> => {
    let loaded = false;
    try {
      const input = {
        studyFid: studyFid ? parseInt(studyFid, 10) : undefined,
        profileId,
      };
      const aiReportData = (await fetchNewReportData({ input })) as NewReportDataResult;
      const { reportData, isSuccess } = aiReportData;
      // Check if resetStudyInfo is not "Done"
      if (
        reportData?.holterProfile?.resetStudyInfo &&
        reportData.holterProfile.resetStudyInfo.status !== 'Done'
      ) {
        setIsResetingStudy(true);
      }
      if (isSuccess && !_.isEmpty(reportData)) {
        if (
          (reportData.study?.isArchived || generateAiStatusReport(reportData) !== 'Ready') &&
          reportData.holterProfile?.resetStudyInfo?.status !== 'Running'
        ) {
          // history.replace('/404');
          emitter.emit(EMITTER_CONSTANTS.PAGE_CRASHED);
          return false;
        }

        const studyId = reportData.study?.id;
        // const profileId = reportData.holterProfile?.id;

        const [downloadEcgDataMap, holterTasks, studyReportsData] = await Promise.all([
          fetchHolterEcgDataMap({ studyId, profileId }),
          fetchHolterTasks(),
          fetchNewReportData({ id: studyId, isStudyReport: true }) as Promise<
            { type: string; status: string }[]
          >,
        ]);
        if (downloadEcgDataMap) {
          CONFIG_ECG.samplingFrequency = downloadEcgDataMap.samplingFrequency;
          CONFIG_ECG.gain = downloadEcgDataMap.gain;
          // pre-existing bug: HolterEcgDataMap.channels is typed number, but this code treats it as a string array.
          ECG_VIEWER_CONFIG_STORE.visibleChannels = _.map(
            downloadEcgDataMap.channels as any,
            (item: any) => Number(item.slice(-1)) - 1,
          );
          // ECG_VIEWER_CONFIG_STORE doesn't declare originalChannels - pre-existing gap.
          (ECG_VIEWER_CONFIG_STORE as any).originalChannels = downloadEcgDataMap.channels;
        }

        const studyReport = _.filter(studyReportsData, (x) => x.type === 'Study')[0];
        const aiReport = _.find(studyReportsData, (x) => x.type === 'AI EOU');
        const startReport = reportData?.report?.start;
        const stopReport = reportData?.report?.stop;
        const timezone = reportData.study?.timezone;
        const timezoneOffset = timezoneToOffset(timezone);
        const listDate = generateListDate(startReport, stopReport, timezoneOffset);
        const combinedEventsInHeatmap: Record<string, any[]> = {};
        _.forEach(listDate, (date) => {
          combinedEventsInHeatmap[date] = [];
        });
        setCombinedEventsInHeatmap(combinedEventsInHeatmap);
        const info = {
          studyId: reportData.study?.id,
          profileId: reportData.holterProfile?.id,
          sessionTime: reportData.holterProfile?.sessionTime,
          lastModified: reportData.holterProfile?.lastModified,
          facilityId: reportData.study?.facility?.id,
          reportId: reportData.report?.id,
          reportFid: reportData.report?.reportId,
          isAbortedStudy: reportData.study?.status === 'Aborted',
          studyFid: reportData.study?.friendlyId,
          patientName: `${reportData.study?.info?.patient?.firstName} ${reportData.study?.info?.patient?.lastName}`,
          facilityName: reportData.study?.facility?.name,
          reportType: reportData.report?.type,
          timezone,
          timezoneOffset,
          startReport,
          stopReport,
          isNonPhi: reportData.study?.isNonPhi,
          holterProfile: reportData.holterProfile,
        };
        setReportInfo(info);
        // *: Sort data by start time
        // pre-existing: HolterEcgDataMapEntry.start/stop are typed string, but this
        // code treats them as numeric epochs for sorting then mutates them to Moment objects.
        (downloadEcgDataMap.data as any[]).sort((a, b) => a.start - b.start);
        for (let i = 0; i < downloadEcgDataMap.data.length; i += 1) {
          const item = downloadEcgDataMap.data[i] as any;
          item.start = moment(item.start);
          item.stop = moment(item.stop);
        }

        findingRef.current =
          reportData?.report?.technicianComments || reportData?.holterProfile?.technicianComments;
        ecgDisclosureRef.current = {
          ecgDisclosure: reportData.holterProfile?.ecgDisclosure,
          timezoneOffset,
          ecgDataMap: downloadEcgDataMap,
        };
        indexedDbInfoRef.current = _.cloneDeep(info);
        setProfileAfibAvgHr(reportData.holterProfile?.afibAvgHr);
        setOriginalDailySummaries(reportData.holterProfile?.mctDailySummaries);
        setEcgDataMap(downloadEcgDataMap);
        setFacilityNote(reportData?.study?.facility?.facilityNote);
        setReportData(reportData);
        setIsOpenProcessingLimit(
          !isHolterProcessingAvailable(holterTasks, reportData?.study?.friendlyId),
        );
        // studyReport/aiReport aren't part of the useMergeState initial shape - pre-existing gap.
        setState({
          isLoadedData: true,
          isGeneratingNormalReportInCallCenter: studyReport?.status === 'Rendering',
          studyReport,
          aiReport,
          pendingProfiles: holterTasks?.pendingProfiles || [],
        } as any);
        loaded = true;
      } else {
        // history.replace('/404');
        emitter.emit(EMITTER_CONSTANTS.PAGE_CRASHED);
      }
    } catch (error) {
      logError('Failed to load report data ', error);
      // history.push('/404');
      emitter.emit(EMITTER_CONSTANTS.PAGE_CRASHED);
    }
    loadingPageAction();
    return loaded;
  };

  /**
   * Shows or hides the study information drawer. Flips isOpenStudyInfoDrawer in the merged page state.
   *
   * @returns Nothing.
   */
  const toggleDisplayStudyInfoDrawer = () => {
    setState({ isOpenStudyInfoDrawer: !state.isOpenStudyInfoDrawer });
  };

  /**
   * Shows or hides the patient information drawer. Flips isOpenPatientInfoDrawer in the merged page state.
   *
   * @returns Nothing.
   */
  const toggleDisplayPatientInfoDrawer = () => {
    setState({ isOpenPatientInfoDrawer: !state.isOpenPatientInfoDrawer });
  };

  /**
   * Shows or hides the facility information drawer. Flips isOpenFacilityInfoDrawer in the merged page state.
   *
   * @returns Nothing.
   */
  const toggleDisplayFacilityInfoDrawer = () => {
    setState({ isOpenFacilityInfoDrawer: !state.isOpenFacilityInfoDrawer });
  };

  /**
   * Reloads the report page in a way Firefox honours. Removes this profile's cached heatmap default, then reassigns window.location.href to itself instead of calling location.reload.
   *
   * @returns Nothing.
   */
  const onClickReload = () => {
    // *Cannot use window.location.reload() because it not work in Firefox
    //  window.location.reload();
    localStorage.removeItem(`defaultValueHeatmap-${profileId}`);
    window.location.href = window.location.href;
  };

  /**
   * Leaves the report for the study's page in the call-center portal. Drops this profile's cached heatmap default from localStorage before assigning the portal URL.
   *
   * @returns Nothing.
   */
  const onClickGoCallCenter = () => {
    localStorage.removeItem(`defaultValueHeatmap-${profileId}`);
    document.location.href = `${URL_CALLCENTER_PORTAL}studies/${studyId}`;
  };

  /**
   * Marks the open report as an artifact report, or clears that mark. Awaits the mark mutation for the active report id, shows an error toast on failure, and closes the confirmation modal either way.
   *
   * @param flag - True to mark the report as an artifact report, false to clear the mark.
   * @returns A promise resolving once the mark request finishes and the confirmation closes.
   */
  const handleOnClickArtifactReport = async (flag: boolean) => {
    try {
      await handleMarkReportAsArtifactReport(reportId, flag);
    } catch (err) {
      toastrError(err, 'Error');
    }
    setState({ isOpenConfirmArtifact: false });
  };

  // Map keyNumber (TAB_KEY) -> ref của tab tương ứng.
  // Comment '1'/'2'/'6'... là chuỗi số TAB_KEY đã persist trong IndexedDB (không đổi).
  const getTabRefByActiveTab = (activeTab: string): any =>
    (
      ({
        [TAB_KEY.BEATS]: beatsRef.current, // '1' Beats
        [TAB_KEY.ECG_EVENTS]: ecgEventsRef.current, // '2' ECG Events
        [TAB_KEY.HR_SUMMARY]: hrSummaryRef.current, // '6' HR Summary
        [TAB_KEY.STRIPS_MANAGEMENT]: stripsRef.current, // '3' Strips Management
        [TAB_KEY.PDF_REPORT]: pdfReportRef.current, // '4' PDF Report
      }) as Record<string, any>
    )[activeTab];

  /**
   * Switches the report page to another tab while protecting unsaved edits. Switches straight away when nothing is edited, otherwise asks the current tab ref whether data changed and either opens the confirmation modal or resets that tab, moves over, takes an action snapshot and clears the undo/redo history.
   *
   * @param tab - Id of the tab to activate; falls back to the default tab when empty.
   * @returns Nothing.
   */
  const navigateTab = (tab: string) => {
    if (tab !== state.activeTab) {
      if (isNotEdit) {
        setState({ activeTab: tab || defaultActiveTab.current });
        resetState();
        return;
      }

      // *: Check data changed
      const tabRef = getTabRefByActiveTab(state.activeTab);

      const isChangedData = tabRef ? tabRef.isChangedData() : false;
      if (isChangedData || isPreventChangeTabWhenSaving.current) {
        tabIndexConfirmNavigate.current = tab;
        setState({ isOpenConfirmNavigateTab: true });
      } else {
        if (tabRef) {
          tabRef.resetData();
        }
        // resetEcgViewerConfigStore(););
        tabIndexConfirmNavigate.current = undefined;
        setState({ activeTab: tab || defaultActiveTab.current, isOpenConfirmNavigateTab: false });
        setActionSnapshot(1);
        resetState();
      }
    }
  };

  /**
   * Abandons a tab switch that was held back by unsaved changes. Clears the pending tab ref and closes the navigation confirmation modal.
   *
   * @returns Nothing.
   */
  const onClickCancelNavigateTab = () => {
    tabIndexConfirmNavigate.current = undefined;
    setState({ isOpenConfirmNavigateTab: false });
  };

  /**
   * Discards the current tab's edits and completes the pending tab switch. Resets the active tab ref, clears the undo/redo history and navigates to the stored pending tab after a short timeout so the reset lands first.
   *
   * @returns Nothing.
   */
  const onClickNavigateWithoutSave = () => {
    const tabRef = getTabRefByActiveTab(state.activeTab);
    if (tabRef) {
      tabRef.resetData();
    }
    const tabNavigate = tabIndexConfirmNavigate.current;

    // reset undo/redo related components
    resetState();
    setTimeout(() => {
      navigateTab(tabNavigate);
    }, 20);
  };

  /**
   * Saves the current tab before completing the tab switch that raised the confirmation. Calls onClickSaveReport on the ref of the active Beats, ECG Events, Strips Management, Report or HR Summary tab, then clears the undo/redo history.
   *
   * @returns Nothing.
   */
  const onClickConfirmNavigateTab = () => {
    const tabRef = getTabRefByActiveTab(state.activeTab);
    if (tabRef) {
      tabRef.onClickSaveReport();
    }

    // reset undo/redo related components
    resetState();
  };

  /**
   * Saves the report from the active tab when the toolbar save button is pressed. Resolves the current tab ref, starts the save timer and disables the toolbar buttons if that tab reports changed data, then calls its onClickSaveReport.
   *
   * @returns A promise resolving once the active tab's save has been triggered.
   */
  const saveReportOnClick = async () => {
    const tabRef = getTabRefByActiveTab(state.activeTab);
    if (tabRef) {
      const isChangedData = tabRef ? tabRef.isChangedData() : false;
      if (isChangedData) {
        // timeSaving count time save report
        startTime.current = Date.now();
        setIsDisabledBtn(true);
      }
      tabRef.onClickSaveReport();
    }
  };

  /**
   * Ends the editing session when the inactivity watchdog fires. Returns early while the AI process is stopped, otherwise closes the no-API-calling modal, opens the no-activity modal, clears the AI processing flag and disconnects the socket.
   *
   * @returns Nothing.
   */
  const handleNoActivity = () => {
    if (window.isStopAiProcess) {
      return;
    }

    if (state.isOpenNoApiCallingModal) {
      setState({ isOpenNoApiCallingModal: false });
    }
    setState({ isOpenNoActivityModal: true });
    setAiProcessing(false);
    socketio.disconnectSocket();
  };

  /**
   * Accepts the processing-limit notice and sends the user to the Report tab. Navigates to tab 4 and closes the limit modal in the report store.
   *
   * @returns Nothing.
   */
  const onClickAcceptLimitProcessingStudy = () => {
    navigateTab(TAB_KEY.PDF_REPORT);
    setLimitProcessingStudyState({
      isOpenModal: false,
    });
  };

  /**
   * Reports whether any report tab currently holds unsaved edits. Yields false outright while the no-activity modal is open, otherwise combines the optional isChangedData callback of the Beats, ECG Events, Strips Management, Report and HR Summary tab refs.
   *
   * @returns True when one of the tabs reports changed data, false otherwise.
   */
  const checkDataChange = () => {
    const result = state.isOpenNoActivityModal
      ? false
      : beatsRef.current?.isChangedData?.() ||
        ecgEventsRef.current?.isChangedData?.() ||
        stripsRef.current?.isChangedData?.() ||
        pdfReportRef.current?.isChangedData?.() ||
        hrSummaryRef.current?.isChangedData?.();
    return result;
  };

  /**
   * Starts the server-side migration of AI beat final paths after beat downloads failed with HTTP 403. Calls the migration mutation for the current study and profile, then marks local migrate state as migrating at five percent and hides the 403 confirmation, logging any error.
   *
   * @returns A promise resolving once the migration request has been issued.
   */
  const handleMigrateBeatFinalPathOn403 = async () => {
    try {
      await handleMigrateAiBeatFinalPaths({
        input: { studyId, profileId },
      });
      setMigrateAiData((prev) => ({
        ...prev,
        isMigrating: true,
        isUpdateMirage: true,
        percentage: 5,
        isShowConfirmMigrate403: false,
      }));
    } catch (error) {
      console.error('Error in handleMigrateBeatFinalPathOn403:', error);
    }
  };

  /**
   * Confirms the user's decision to migrate beat final paths after the 403 download failures. Awaits handleMigrateBeatFinalPathOn403, which issues the migration mutation and flips the local migrate state to migrating.
   *
   * @returns A promise resolving once the migration has been requested.
   */
  const onClickConfirmMigrate403 = async () => {
    await handleMigrateBeatFinalPathOn403();
  };

  /**
   * Verifies that the beat rows cached in IndexedDB match the ECG data map and steers the error UI when they do not. Clears any pending retry, compares the stored beat count against the map length, retries twice with a delay, then either offers the 403 beat-final-path migration or escalates the reload-versus-contact-support state kept per study in localStorage, always re-rendering the ECG viewer.
   *
   * @param retryCount - How many retries have already run; retrying stops at the retry limit.
   * @returns A promise resolving once the comparison, retries and state updates finish.
   */
  const checkTotalBeatMatchInIndexDB = async (retryCount = 0) => {
    try {
      // Cleanup previous timeout if any
      if (checkBeatsTimeOut.current) {
        clearTimeout(checkBeatsTimeOut.current);
        checkBeatsTimeOut.current = null;
      }

      // Defensive: Check data existence
      if (!ecgDataMap || !Array.isArray(ecgDataMap.data)) {
        console.warn('ecgDataMap is not available');
        setIsOpenWrongSomeThing(true);
        setIsLoadBeatData(false);
        setTypeWrong(2);
        return;
      }

      const expectedBeatCount = ecgDataMap.data.length;
      const actualBeatCount = await getBeatDataCount();
      const beatDataStatusKey = 'beatDataStatus';

      // Get status from localStorage
      const statusLocal = JSON.parse(localStorage.getItem(beatDataStatusKey)) || {};
      const prevStatus = statusLocal[studyFid] || 0;

      if (actualBeatCount === expectedBeatCount) {
        setIsOpenWrongSomeThing(false);
        setIsLoadBeatData(false);
        setTypeWrong(0);
        return;
      }

      if (retryCount < BEAT_RETRY_LIMIT) {
        // Retry with delay
        checkBeatsTimeOut.current = setTimeout(() => {
          checkTotalBeatMatchInIndexDB(retryCount + 1);
        }, BEAT_RETRY_DELAY);
        return;
      }

      // After retries, check for error code 403
      const studyMeta = await getStudyMetadata(profileId);
      const has403Error = studyMeta?.value?.beatInfo?.errorCodes?.includes(403);

      if (has403Error) {
        // Chỉ hiển thị màn hình xác nhận nếu chưa có màn hình nào đang mở và chưa đang migrate
        if (!migrateAiData.isMigrating && !state.isShowConfirmMigrate403) {
          setState({ isShowConfirmMigrate403: true });
        }
      } else {
        setIsOpenWrongSomeThing(true);
        setIsLoadBeatData(true);
        // 1: reload, 2: contact support
        const newStatus = prevStatus === 1 ? 2 : prevStatus === 0 ? 1 : 2;
        setTypeWrong(newStatus);
        statusLocal[studyFid] = newStatus;
        localStorage.setItem(beatDataStatusKey, JSON.stringify(statusLocal));
      }
    } catch (error) {
      console.error('Error in checkTotalBeatMatchInIndexDB:', error);
      setIsOpenWrongSomeThing(true);
      setIsLoadBeatData(false);
      setTypeWrong(2);
    } finally {
      // Always trigger re-render
      emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
      console.log('## Re-render ECG Viewer');
    }
  };

  // Kiểm tra dung lượng lưu trữ khả dụng và trả về trạng thái phù hợp
  /**
   * Classifies the browser's remaining storage budget before beat and ECG files are cached for the study. Reads the Storage API estimate, converts the free bytes to GB and logs them, then returns low below 0.5 GB while raising the storage warning flag, noEcg below 5 GB, sufficient otherwise, and unknown when the API is unavailable.
   *
   * @returns A promise resolving to the storage verdict: low, noEcg, sufficient or unknown.
   */
  const checkAvailableStorageSpace = async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const { usage, quota } = await navigator.storage.estimate();
      const availableStorageInGB = (quota - usage) / 1024 ** 3;
      console.log(
        `## Storage: ${convertBytesToGB(usage)} GB, Available: ${availableStorageInGB.toFixed(2)} GB`,
      );
      if (availableStorageInGB < 0.5) {
        setState({ isOpenWarningLimitStorage: true });
        console.log('###Warning: Storage below 0.5 GB');
        // Dung lượng quá thấp
        return 'low';
      }
      if (availableStorageInGB < 5) {
        console.log('###Storage below 5 GB, skipping ECG data download');
        // Không tải ECG
        return 'noEcg';
      }
      setState({ isOpenWarningLimitStorage: false });
      console.log('###Storage sufficient for ECG data download');
      // Dung lượng đủ để tải tất cả dữ liệu
      return 'sufficient';
    }
    // Không xác định được dung lượng
    return 'unknown';
  };

  // Hàm tải dữ liệu và ghi lại thời gian thực hiện
  /**
   * Downloads one batch of beat or ECG files through a worker thread and logs how long it took. Groups the entries by epoch start-of-day over the given period, then routes Final-Beat through the beat-only downloader while toggling the beat-loading flag on the first mount, and everything else through the ECG downloader.
   *
   * @param thread - Worker thread that performs the download.
   * @param ecgData - Data-map entries to download.
   * @param period - Grouping window handed to the epoch grouping helper, such as weeks or days.
   * @param dataType - Final-Beat to run the beat downloader, anything else to run the ECG downloader.
   * @param querySignature - Signed query authorising the file requests.
   * @returns A promise resolving once every grouped batch has been downloaded.
   */
  const downloadAndLogTime = async (
    thread: any,
    ecgData: any,
    period: any,
    dataType: any,
    querySignature: any,
  ) => {
    const groupedData = groupDataByEpochStartOfDay(ecgData, period, 'start');
    const startTime = Date.now();

    if (dataType === 'Final-Beat') {
      if (isInitialMount.current) {
        setIsLoadBeatData(true);
      }
      await downloadOnlyBeatByDateEpoch(thread, groupedData, querySignature);
      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
    }
    console.log(`Time to download all ${dataType}: ${Date.now() - startTime}ms`);
  };

  /**
   * Performs the first full download of the study's beat files into IndexedDB. Skips entirely when the storage check reports low space, otherwise spawns a beat worker thread, downloads every entry grouped by week, terminates the thread and clears the beat-loading flag.
   *
   * @param ecgDataMap - ECG data map whose entries and query signature drive the download.
   * @returns A promise resolving once the beat files are downloaded or the download was skipped.
   */
  const handleInitialBeatDataProcessing = async (ecgDataMap: any) => {
    try {
      const storageStatus = await checkAvailableStorageSpace();
      // Lower capacity allowed should not download beats
      if (storageStatus === 'low') return;
      const beatThread = await handleDownloadBeatList(ecgDataMap);
      if (beatThread) {
        await downloadAndLogTime(
          beatThread,
          ecgDataMap.data,
          'weeks',
          'Final-Beat',
          ecgDataMap.querySignature,
        );
        terminateThread(beatThread);
        setIsLoadBeatData(false);
      }
    } catch (error) {
      console.error('Error handleInitialBeatDataProcessing:', error);
    }
  };

  // Helper: Beat data check is enough
  /**
   * Decides whether the beat files cached in IndexedDB still fall short of the downloadable set. True when fewer beat rows are actually stored than there are downloadable files, or when the report was modified after the cached metadata. Completeness is judged against the real store count (never a possibly-stale beatInfo counter); non-downloadable entries (null beatFinalPath) are excluded so a fully-downloaded study with unavailable files is not reported incomplete.
   *
   * @param beatCount - Number of beat rows currently held in IndexedDB.
   * @param downloadableCount - Number of entries that actually have a beat file to download.
   * @param compareLastModified - True when the report is newer than the cached metadata.
   * @returns True when the cached beat data has to be downloaded again.
   */
  const isBeatDataIncomplete = (beatCount: any, downloadableCount: any, compareLastModified: any) =>
    beatCount < downloadableCount || compareLastModified;

  /**
   * Checks whether the cached beat data is still complete and downloads it again when it is not. Compares counts and last-modified against the study metadata, clears the beat store, re-runs the initial beat download and records the files that failed.
   *
   * @param typeDownload - Currently only beat_download, choosing which cache is verified.
   * @returns A promise resolving once the verification and any re-download finish.
   */
  const verifyBeatInFlightRef = useRef(false);
  const verifyBeatPendingRef = useRef(false);
  const latestEcgDataMapRef = useRef(ecgDataMap);
  latestEcgDataMapRef.current = ecgDataMap;
  const verifyAndRefreshEcgBeatData = async (typeDownload: string) => {
    // Re-entrancy guard: ecgDataMap can change several times per load (report fetch, socket
    // updates). Overlapping runs clear the beat store mid-download and race syncBeatInfo, which then
    // persists a stale beatInfo.status. While a run is in flight, remember that another was
    // requested and re-run once with the latest map when it finishes.
    if (verifyBeatInFlightRef.current) {
      verifyBeatPendingRef.current = true;
      return;
    }
    verifyBeatInFlightRef.current = true;
    const map = latestEcgDataMapRef.current;
    try {
      // IReportInfo (Recoil/model.ts) doesn't declare lastModified - pre-existing gap.
      const info = (await getReportInfo()) as IReportInfo & { lastModified?: any };
      const studyData = await getStudyMetadata(profileId);
      // No report lastModified => no staleness signal, so do not force a re-download.
      const lastModifiedEpoch = info?.lastModified ? moment(info.lastModified).valueOf() : 0;
      const lastModifiedMetaEpoch = studyData?.value?.lastModified
        ? moment(studyData.value.lastModified).valueOf()
        : 0;
      const compareLastModified = lastModifiedEpoch > lastModifiedMetaEpoch;

      await checkStudyMetadata(profileId);
      const beatCount = await getBeatDataCount();
      // Only entries with a real beat file count toward completeness; null paths can never be fetched.
      const downloadableCount = _.filter(
        map?.data,
        (item: any) => !_.isEmpty(item.beatFinalPath) && item.beatFinalPath !== 'null',
      ).length;

      if (typeDownload === 'beat_download') {
        if (isBeatDataIncomplete(beatCount, downloadableCount, compareLastModified)) {
          console.log('## Beat data not complete');
          setIsDownloadingBeatFile(true);
          await clearDBBeatData();
          await handleInitialBeatDataProcessing(map);

          // Regain Metadata after processing
          const updatedStudy = await checkStudyMetadata(profileId);
          let failedList: any[] = [];
          if (updatedStudy?.beatInfo?.idDownloadFailed?.length > 0) {
            failedList = map.data.filter((item: any) =>
              updatedStudy.beatInfo.idDownloadFailed.includes(item.id),
            );
          } else {
            failedList = map.data.filter(
              (item: any) => item.beatFinalPath === 'null' || item.beatFinalPath === null,
            );
          }
          setListDownloadFile(failedList);
          setIsDownloadingBeatFile(false);
        } else {
          console.log('## Beat data complete');
          // Reconcile beatInfo with what the store actually holds (no download ran this open).
          await syncBeatInfo(map);
          setIsDownloadingBeatFile(false);
          setIsOpenWrongSomeThing(false);
          setIsLoadBeatData(false);
        }
      }
    } catch (error) {
      console.error('Error verifyAndRefreshEcgBeatData:', error);
    } finally {
      verifyBeatInFlightRef.current = false;
      // A change arrived mid-run: re-run once with the latest map so real updates aren't dropped.
      if (verifyBeatPendingRef.current) {
        verifyBeatPendingRef.current = false;
        verifyAndRefreshEcgBeatData(typeDownload);
      }
    }
  };

  /**
   * Reports the beat files that could not be downloaded so the AI pipeline bug can be traced. Maps the failed-download list down to id and beatFinalPath pairs and posts them for the current profile, logging a failure instead of throwing.
   *
   * @returns A promise resolving once the failed beat list has been reported.
   */
  const logAiBugFinalBeat = async () => {
    try {
      // lấy id vs beatFinalPath của các file beat không tải được (403, 404, ... error and null beatFinalPath)
      const listFinalBeat = listDownloadFile.map((item: any) => ({
        id: item.id,
        beatFinalPath: item.beatFinalPath,
      }));
      await handleLogAiBugFinalBeat({
        profileId,
        errorHrSummaries: listFinalBeat,
      });
    } catch (error) {
      logError('Failed to load report data ', error);
    }
  };

  /**
   * Starts the recurring session-token expiry poll for the open report. Keeps a five-minute interval in a ref that awaits fetchCheckExpiredToken and logs the message of any failure.
   *
   * @returns Nothing.
   */
  const handleCheckExpiredTokenInterval = () => {
    // Check every 5 minutes
    checkExpiredTokenInterval.current = setInterval(
      async () => {
        try {
          await fetchCheckExpiredToken();
        } catch (error) {
          console.error('Failed to check expired token: ', error.message);
        }
      },
      5 * 60 * 1000,
    );
  };

  /**
   * Stops the token-expiry poll started for this report. Clears the stored interval and nulls the ref when one is running.
   *
   * @returns Nothing.
   */
  const handleRemoveCheckExpiredTokenInterval = () => {
    if (checkExpiredTokenInterval.current) {
      clearInterval(checkExpiredTokenInterval.current);
      checkExpiredTokenInterval.current = null;
    }
  };

  useEffect(() => {
    if (!_.isEmpty(listDownloadFile) && !_.isEmpty(ecgDataMap)) {
      logAiBugFinalBeat();
    }
  }, [listDownloadFile]);

  useEmitter(
    EMITTER_CONSTANTS.VERIFY_ALL_BEAT_FILES_DOWNLOADED,
    () => {
      if (!_.isEmpty(ecgDataMap)) {
        checkTotalBeatMatchInIndexDB();
      }
    },
    [ecgDataMap, migrateAiData],
  );

  useEmitter(
    EMITTER_CONSTANTS.RE_MIGRATE_FILE_DOWNLOAD_403,
    () => {
      // Chỉ hiển thị màn hình xác nhận nếu chưa có màn hình nào đang mở và chưa đang migrate
      if (!migrateAiData.isMigrating && !state.isShowConfirmMigrate403) {
        setState({ isShowConfirmMigrate403: true });
      }
    },
    [studyId, profileId, migrateAiData.isMigrating, state.isShowConfirmMigrate403],
  );

  useEmitter(
    EMITTER_CONSTANTS.PROFILE_READY,
    async (msg: any) => {
      if (msg?.profileId === profileId) {
        if (msg.isReady) {
          setIsStartedAiProcessing(true);
        } else if (msg.message === 'resetStudy') {
          document.location.href = `${URL_CALLCENTER_PORTAL}studies/${studyId}`;
        } else if (isStartedAiProcessing) {
          await clearApolloCache();
          clearCacheAPI();
          handleNoActivity();
        }
      }
    },
    [isStartedAiProcessing, profileId, studyId, state.isOpenNoApiCallingModal],
  );

  // useEffect for ecgDataMap changes check beat
  useEffect(() => {
    if (!_.isEmpty(ecgDataMap)) {
      verifyAndRefreshEcgBeatData('beat_download');
    }
  }, [ecgDataMap]);

  // check and delete database not use indexdb
  /**
   * Drops the IndexedDB databases no longer referenced by any open report tab. Delegates the whole job to deleteUnusedDatabases.
   *
   * @returns A promise resolving once the unused databases are deleted.
   */
  const handledDeleteUnusedDatabases = async () => {
    await deleteUnusedDatabases();
  };

  useEffect(() => {
    if (!profileId) {
      return undefined;
    }

    const tabId = `${profileId}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    /**
     * Removes this browser tab from the shared registry as the report unmounts, dropping the now
     * unused IndexedDB databases when it was the last living tab.
     *
     * @returns Nothing.
     */
    const leave = () => {
      const remaining = unregisterTab(profileId, tabId);
      // This tab left: evict any study that no longer meets the keep-priority (inactive + over
      // limit) and refresh the active/activeTabCount/priority snapshot, so IndexedDB is updated
      // right away rather than at the next report open. Best-effort: on a real tab close the async
      // writes may not finish, but the sync registry update above already makes the study inactive.
      reconcileMetadata();
      if (remaining === 0) {
        handledDeleteUnusedDatabases();
      }
    };

    registerTab(profileId, tabId);
    // Refresh this tab's timestamp so a crashed tab (which never fires beforeunload) goes stale
    // and stops pinning the study "open". Use worker-timers, not window.setInterval: a backgrounded
    // tab's window timers are throttled below the 45s TTL, which would let a still-open tab go stale
    // and get its IndexedDB evicted by another tab.
    const heartbeat = setInterval(() => registerTab(profileId, tabId), TAB_HEARTBEAT_MS);
    window.addEventListener('beforeunload', leave);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', leave);
      leave();
    };
  }, [profileId]);

  useEffect(() => {
    if (checkInitialUserRoom && checkInitialUserRoomRef.current) {
      handleClearIndexDB(indexedDbInfoRef.current);
      setCheckInitialUserRoom(false);
      checkInitialUserRoomRef.current = false;
    }
  }, [checkInitialUserRoom, isNotEdit]);

  /**
   * Loads the ECG bookmarks for the open study and profile into the report store. Requests them ascending from offset zero and merges them with anything already stored, de-duplicating by the bookmark center.
   *
   * @returns A promise resolving once the bookmarks are merged into the store.
   */
  const handleFetchBookmarks = async () => {
    try {
      const ecgBookmarks = await fetchListEcgBookmarks(
        {
          studyId,
          sortOrder: 'asc',
          skip: 0,
          profileId,
        },
        0,
      );
      setEcgBookmarks((prev: any) => {
        if (_.isEmpty(prev)) {
          return ecgBookmarks;
        }
        return _.uniqBy([...(prev || []), ...(ecgBookmarks || [])], 'center');
      });
    } catch (error) {
      logError('Failed to fetch bookmarks ', error);
    }
  };

  useEffect(() => {
    /**
     * Loads the report page's secondary data once the page is ready. Awaits the beat and rhythm event counts and the ECG bookmarks, then after a second clears the undo/redo history and re-enables the toolbar buttons.
     *
     * @returns A promise resolving once the counts and bookmarks are loaded.
     */
    const fetchData = async () => {
      await fetchEventsCount();
      await handleFetchBookmarks();
      setTimeout(() => {
        resetState();
        setIsDisabledBtn(false);
      }, 1000);
    };
    if (isStartedAiProcessing && isLoadedIndexDB) {
      setIsDisabledBtn(true);
      fetchData();
    }
  }, [isStartedAiProcessing, isLoadedIndexDB]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    //* Refresh query signature every 1 hour
    if (state.isLoadedData) {
      /**
       * Renews the signed query used to fetch hourly-summary files. Requests a fresh signature for the study and profile and merges it into the stored ECG data map inside a transition, logging any failure.
       *
       * @returns A promise resolving once the new signature is stored.
       */
      const fetchNewQuerySignature = async () => {
        try {
          const querySignature = await fetchQuerySignature({
            studyId,
            prefixType: 'hourlySummary',
            profileId,
          });
          startTransition(() => {
            setEcgDataMap((currentEcgDataMap: any) => ({ ...currentEcgDataMap, querySignature }));
          });
        } catch (error) {
          console.log(error);
        }
      };
      interval = setInterval(() => {
        if (aiProcessing) {
          fetchNewQuerySignature();
        }
      }, 3600000); // 1 hour
    }
    return () => {
      try {
        if (interval) {
          clearInterval(interval);
        }
      } catch (error) {
        console.log(error);
      }
    };
  }, [state.isLoadedData, studyId, profileId, aiProcessing]);

  useEffect(() => {
    // Start each (re)load with persistence blocked; only a profile validated by a successful report
    // fetch may open/write IndexedDB, so an invalid/stale profile id never creates junk databases.
    disableReportPersistence();
    if (profileId) {
      fetchReportData(studyFid).then((ok) => {
        if (ok) {
          enableReportPersistence();
          getDatabaseState();
        }
      });
    } else {
      // history.replace('/404');
      emitter.emit(EMITTER_CONSTANTS.PAGE_CRASHED);
    }
  }, [studyFid]);

  useEffect(() => {
    if (isWsConnected && !_.isEmpty(reportData)) {
      console.log('## Joining WebSocket rooms');
      socketio.emitRoom(EMITTER_CONSTANTS.HOLTER_GENERAL_ROOM);
      socketio.emitRoom(EMITTER_CONSTANTS.HOLTER_PROFILE_ROOM.replace('{profileId}', profileId));
      socketio.emitRoom(reportData.study?.device?.deviceId);
      // *: Join room report
      const reportType = reportData?.report?.type;
      if (
        reportType === 'Study' ||
        reportType === 'Interim' ||
        reportType === 'AI EOU' ||
        reportData?.report?.reportId ||
        reportType === 'UNREVIEWED'
      ) {
        const room = `${profileId}_${
          reportType === 'Interim' ? (reportType === 'UNREVIEWED' ? 'ai_u' : 'ai_i') : 'ai_eou'
        }${reportType === 'UNREVIEWED' || reportType === 'Interim' ? `${reportData?.report?.reportId}` : ''}`;
        roomReport.current = room;
        socketio.sendJoinReportRoom(roomReport.current);
      }
    }
  }, [isWsConnected, reportData, profileId]);

  useUpdateEffect(() => {
    upsertDataState('activeTab', state.activeTab).catch(() => {});
  }, [state.activeTab]);

  useEffect(() => {
    if (!isNotEdit) {
      const info = getReportInfo() as IReportInfo;
      const sessionTime = moment();
      // update session time
      const sessionInfo = {
        sessionTime: sessionTime.toISOString(),
        studyId: info.studyId,
        profileId: info.profileId,
      };
      const updateHolterProfileInput = {
        id: info.profileId,
        sessionTime,
      };
      sessionInfoRef.current = sessionInfo;
      handleUpdateHolterProfile(updateHolterProfileInput);
      upsertDataState('sessionInfo', sessionInfo).catch(() => {});
    }
  }, [isNotEdit]);

  /**
   * Brings a holter profile's AI data from schema version 1 to version 2 and mirrors the progress locally. Exits when no report or holter profile is loaded, adopts the server's in-flight percentage, estimate and beat-final-path note when a migration already runs, requests fetchMigrateAiData for version 1 profiles, and merely clears the migrating flag for version 2.
   *
   * @returns A promise resolving once the migration state has been adopted or requested.
   */
  const migrateAiDataVer1ToVer2 = async () => {
    if (!reportData) return;

    const { study } = reportData;
    const { holterProfile } = study || {};

    if (!holterProfile) return;

    if (holterProfile.migrateAiInfo?.isMigrating) {
      setMigrateAiData((prevState) => ({
        ...prevState,
        isMigrating: true,
        percentage: holterProfile.migrateAiInfo.percentage,
        estimateTime: holterProfile.migrateAiInfo.estimateTime,
        isUpdateMirage: holterProfile.migrateAiInfo.migrateNote === 'Migrate beat final path',
      }));
      return;
    }

    if (holterProfile.version === 1) {
      setMigrateAiData((prevState) => ({
        ...prevState,
        isMigrating: true,
      }));

      const input = {
        studyId: study.id,
        profileId: holterProfile.id,
      };

      try {
        const { isSuccess, message } = await fetchMigrateAiData(input);
        if (isSuccess) {
          console.log('[aiReport]-MigrateAiDataVer1ToVer2', message);
        }
      } catch (error) {
        console.error('Error migrating AI data:', error);
      }
    } else if (holterProfile.version === 2) {
      console.log('[aiReport]- migrateAiData version 2');
      setMigrateAiData((prevState) => ({
        ...prevState,
        isMigrating: false,
      }));
    }
  };

  /**
   * Records that no API call reached the server for fifteen minutes and opens the matching modal. Builds a NO_API_CALLING payload from the report type, the report id for Interim reports only, the study friendly id, the profile id and the mapped conflict users, posts it to the monitoring endpoint, and always sets isOpenNoApiCallingModal.
   *
   * @returns A promise resolving once the event is logged and the modal opened.
   */
  const displayNoApiCallingModal = async () => {
    try {
      const reportType = reportData.report?.type;
      const reportId = reportData.report?.reportId;
      const studyFid = reportData.study?.friendlyId;
      await handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: 'NoApiCalling',
          message: 'No API calling from client to server in 15 minutes',
          studyFid,
          profileId,
          reportType,
          reportId: reportType === 'Interim' ? reportId : undefined,
          conflictUsers: _.map(state.conflictUsers, (user) => ({
            sid: user.sid,
            token: user.token,
            userId: user.userId,
            username: user.username,
          })),
        }),
        type: FRONT_END_ERRORS.NO_API_CALLING,
        userId: meRedux.id,
      });
    } catch (error) {
      console.error('Failed to log frontend error: ', error);
    } finally {
      setState({ isOpenNoApiCallingModal: true });
    }
  };

  /**
   * Handles the user confirming they are still working after the inactivity prompt. Logs a NO_API_CALLING event carrying the report type, the Interim report id, the study friendly id and the conflict users, then always closes the no-API-calling modal.
   *
   * @returns A promise resolving once the confirmation is logged and the modal closed.
   */
  const handleConfirmActivity = async () => {
    try {
      const reportType = reportData.report?.type;
      const reportId = reportData.report?.reportId;
      const studyFid = reportData.study?.friendlyId;
      handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: 'NoApiCalling',
          message: 'User confirmed activity after no API calling in 15 minutes',
          studyFid,
          profileId,
          reportType,
          reportId: reportType === 'Interim' ? reportId : undefined,
          conflictUsers: _.map(state.conflictUsers, (user) => ({
            sid: user.sid,
            token: user.token,
            userId: user.userId,
            username: user.username,
          })),
        }),
        type: FRONT_END_ERRORS.NO_API_CALLING,
        userId: meRedux.id,
      });
    } catch (error) {
      console.error('Failed to log frontend error: ', error);
    } finally {
      setState({ isOpenNoApiCallingModal: false });
    }
  };

  /**
   * Reports an IndexedDB connection failure to the monitoring backend and then surfaces the error modal. Sends the real latched error name/message, the study friendly id, profile id, this study's metadata record and a storage-quota estimate as an INDEXEDDB_ERROR payload, logs a send failure, and always sets isOpenIndexDBError.
   *
   * @returns A promise resolving once the failure has been reported and the modal flag set.
   */
  const displayModalIndexDBError = async () => {
    // Read the latched error detail synchronously, before any await, so the effect that clears the
    // flag right after calling this cannot race the read.
    let detail: { name?: string; message?: string } = {};
    try {
      const raw = sessionStorage.getItem('indexedDBErrorDetail');
      if (raw) detail = JSON.parse(raw);
    } catch {
      /* ignore malformed detail */
    }
    try {
      const studyFid = reportData.study?.friendlyId;
      const studyMeta = await getStudyMetadata(profileId);

      let storage;
      try {
        if (navigator.storage?.estimate) {
          const { usage, quota } = await navigator.storage.estimate();
          storage = { usage, quota };
        }
      } catch {
        /* storage estimate unsupported */
      }

      await handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: detail.name || 'IndexDBError',
          message: detail.message || 'IndexedDB Connection failed',
          studyFid,
          profileId,
          metadata: studyMeta,
          storage,
        }),
        type: FRONT_END_ERRORS.INDEXEDDB_ERROR,
        userId: meRedux.id,
      });
    } catch (error) {
      console.error('Failed to log frontend error: ', error);
    } finally {
      setState({ isOpenIndexDBError: true });
    }
  };

  useEffect(() => {
    if (state.isLoadedData && sessionStorage.getItem('indexedDBError')) {
      displayModalIndexDBError();
      sessionStorage.removeItem('indexedDBError');
      sessionStorage.removeItem('indexedDBErrorDetail');
    }
  }, [state.isLoadedData]);

  useEffect(() => {
    if (!isLoadedIndexDB) {
      // check if study is migrate ai or not
      migrateAiDataVer1ToVer2();
      try {
        if (reportData.report?.status === 'Rendering') {
          loadingPageAction(LOADING_TYPES.GENERATE_REPORT);
          timerRenderReport.current = setTimeout(() => {
            loadingPageAction();
          }, 900000);
        }
      } catch (error) {
        console.log(error);
        timerRenderReport.current = null;
      }
    }
    return () => {
      if (timerRenderReport.current) {
        clearTimeout(timerRenderReport.current);
        timerRenderReport.current = null;
      }
    };
  }, [reportData, isLoadedIndexDB]);

  useEffect(() => {
    if (state.isLoadedData) {
      handleCheckExpiredTokenInterval();
    }
    return () => {
      handleRemoveCheckExpiredTokenInterval();
    };
  }, [state.isLoadedData]);

  useEmitter(
    EMITTER_CONSTANTS.REPORT_UPDATED,
    async (msg: any) => {
      const reportDataClone = _.cloneDeep(reportData);
      if (msg.studyFid === reportDataClone.study?.friendlyId) {
        if (msg.id === reportDataClone.report?.id) {
          if (msg.isNonPhi === reportDataClone.study?.isNonPhi) {
            if (msg.resetStudyInfo && reportDataClone.holterProfile) {
              const previousResetStudyStatus = reportDataClone.holterProfile.resetStudyInfo?.status;
              if (
                previousResetStudyStatus !== msg.resetStudyInfo.status &&
                msg.resetStudyInfo.status === 'Done'
              ) {
                setIsResetingStudy(false);
                onClickReload();
                return;
              }
              // Update holterProfile.resetStudyInfo so UI and future checks use the latest status
              reportDataClone.holterProfile.resetStudyInfo = msg.resetStudyInfo;
            }
            _.assign(reportDataClone.report, msg);
            setReportData(reportDataClone);
            emitter.emit(EMITTER_CONSTANTS.AI_REPORT_LOADING, { isLoading: false });
          } else {
            // Report format cũ ở bên callcenter nếu đang generate sẽ chặn ko cho copy report AI
            setState({ isGeneratingNormalReportInCallCenter: msg.status === 'Rendering' });
          }
        } else {
          // Report format cũ ở bên callcenter nếu đang generate sẽ chặn ko cho copy report AI
          setState({ isGeneratingNormalReportInCallCenter: msg.status === 'Rendering' });
        }
      }
    },
    [reportData, profileId],
  );

  useEmitter(
    EMITTER_CONSTANTS.INBOX,
    (msg: any) => {
      const reportDataClone = _.cloneDeep(reportData);
      const { report } = reportDataClone;
      if (report && report.id === msg.id) {
        report.inbox = msg.inbox;
        setReportData(reportDataClone);
      }
    },
    [reportData, profileId],
  );

  // check migrate data
  useEmitter(
    EMITTER_CONSTANTS.MIGRATE_AI_DATA_VER1_TO_VER2,
    (msg: any) => {
      if (msg.isMigrating) {
        setMigrateAiData((prevState) => ({
          ...prevState,
          isMigrating: msg.isMigrating,
          status: msg.status,
          percentage: msg.percentage,
          estimateTime: msg.estimateTime,
          note: msg.note,
        }));
      } else if (msg.status === 'Done' && msg.percentage === 100) {
        onClickReload();
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.REPORT_ROOM_USER,
    async (msg: any) => {
      const updatedEvent = _.findLast(msg, (x: any) => x.room === roomReport.current);
      if (updatedEvent) {
        const { users } = updatedEvent;
        const accessToken = auth.getOriginalToken();
        // *: Check user can edit report
        let newIsNotEdit = true;
        const sortedConflictUsers = users?.sort((a: any, b: any) => a.time - b.time);
        const firstUser: any = sortedConflictUsers?.length > 0 ? sortedConflictUsers[0] : {};
        if (!_.isEmpty(firstUser.userId) && !_.isEmpty(firstUser.token)) {
          if (
            firstUser.userId === auth.userId() &&
            firstUser.token === (accessToken || '').slice(-10)
          ) {
            newIsNotEdit = false;
          } else {
            newIsNotEdit = true;
          }
        } else {
          newIsNotEdit = true;
        }
        //* Reload 2nd user when 1st user exit
        if (
          isNotEdit &&
          !newIsNotEdit &&
          isStartedAiProcessing &&
          state.conflictUsers.length > 1 &&
          users.length === 1
        ) {
          onClickReload();
          return;
        }
        setIsNotEdit(newIsNotEdit);
        setCheckInitialUserRoom(true);
        setState({ conflictUsers: sortedConflictUsers });
      }
    },
    [isNotEdit, state.conflictUsers, isStartedAiProcessing],
  );

  useEmitter(
    EMITTER_CONSTANTS.HOLTER_PROFILE_UPDATED,
    (msg: any) => {
      const reportDataClone = _.cloneDeep(reportData);
      if (msg?.ecgDisclosure) {
        _.assign(reportDataClone.holterProfile, { ecgDisclosure: msg.ecgDisclosure });
      }
      if (!_.isEmpty(msg?.reportConfiguration)) {
        _.assign(reportDataClone.holterProfile, { reportConfiguration: msg.reportConfiguration });
      }
      if (!_.isEqual(reportDataClone, reportData)) {
        setReportData(reportDataClone);
      }
    },
    [reportData],
  );

  useEmitter(
    EMITTER_CONSTANTS.AI_LOADING,
    (msg: any) => {
      const { isLoading } = msg || {};
      if (isLoading) {
        updatingTabCount.current = 0;
        setState({ isLoadingSaveAiReport: true });
        setIsDisabledBtn(true);
        setIsUndoDisabled(true);
        setIsRedoDisabled(true);
        isPreventChangeTabWhenSaving.current = true;
      } else {
        setState({ isLoadingSaveAiReport: false });
        resetHistoryState();
        setIsDisabledBtn(false);
        emitter.emit(EMITTER_CONSTANTS.ENABLE_UPDATE_FINDINGS);
        setTimeout(() => {
          endTime.current = Date.now();
          const time = endTime.current - startTime.current;
          console.log(`Execution total time saving: ${time} ms`);
        }, 0);
        const tabNavigate = tabIndexConfirmNavigate.current;
        isPreventChangeTabWhenSaving.current = false;
        if (isInitialSessionInfo.current) {
          const isAISaving = JSON.parse(localStorage.getItem(`ai_saving-${profileId}`));
          if (isAISaving) {
            clearDataAndResaveSessionInfo();
          }
          isInitialSessionInfo.current = false;
        }
        // pre-existing bug: localStorage.setItem expects a string value, not boolean.
        localStorage.setItem(`ai_saving-${profileId}`, false as unknown as string);
        if (state.isOpenConfirmNavigateTab && tabNavigate) {
          setTimeout(() => {
            navigateTab(tabNavigate);
          }, 20);
        }
      }
    },
    [state.isOpenConfirmNavigateTab, isNotEdit],
  );

  useEmitter(
    EMITTER_CONSTANTS.COMMAND_PROGRESS_UPDATED,
    (msg: any) => {
      const { command, percentage } = msg;
      if (command === 'update-report-data') {
        loadingPageAction(`${LOADING_TYPES.GENERATE_REPORT} ${percentage} %`);
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.STOP_AI_PROCESSING,
    () => {
      setAiProcessing(false);
    },
    [],
  );

  useEmitter(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER, () => {
    setIsRenderViewerEcg(false);
    setTimeout(() => {
      setIsRenderViewerEcg(true);
    }, 500);
  });

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_CONNECTED,
    () => {
      console.log('###Emitter set WebSocket connected');
      setIsWsConnected(true);
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_RECONNECTED,
    () => {
      console.log('###Emitter set WebSocket reconnected');
      setIsWsConnected(true);
    },
    [],
  );

  useEmitter(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, (pendingProfiles: any) => {
    setState({ pendingProfiles: pendingProfiles || [] });
  });

  useEmitter(
    EMITTER_CONSTANTS.EXPIRED_TOKEN,
    () => {
      handleRemoveCheckExpiredTokenInterval();

      const setStateObject = {};
      if (state.isOpenNoApiCallingModal) {
        Object.assign(setStateObject, { isOpenNoApiCallingModal: false });
      }
      if (state.isOpenNoActivityModal) {
        Object.assign(setStateObject, { isOpenNoActivityModal: false });
      }
      Object.assign(setStateObject, { isExpiredToken: true });
      setState(setStateObject);
    },
    [state.isOpenNoApiCallingModal],
  );

  const limitUserProcessingContent = useMemo(() => {
    if (_.isEmpty(limitProcessingStudy)) return '';
    const { limitCount, studyFids, isOpenModal } = limitProcessingStudy || {};
    if (isOpenModal) {
      onDiscardChangeSavedData();
    }
    const content = isOpenModal
      ? `Sorry! The user is unable to handle more than ${limitCount} ${
          limitCount > 1 ? 'studies' : 'study'
        } simultaneously. \nStudies are in progress: ${studyFids?.join(', ')}`
      : '';
    return content;
  }, [limitProcessingStudy]);

  /**
   * Saves the active report tab and then leaves for the study page in the call-center portal. Picks the ref for the current Beats, ECG Events, Strips Management, Report or HR Summary tab, calls its onClickSaveReport, and redirects to the portal study URL.
   *
   * @returns Nothing.
   */
  const onClickConfirmNavigateV2Tab = () => {
    const tabRef = getTabRefByActiveTab(state.activeTab);
    if (tabRef) {
      tabRef.onClickSaveReport();
    }
    document.location.href = `${URL_CALLCENTER_PORTAL}studies/${studyId}`;
  };

  const isOldVersion =
    !_.isNil(reportData?.holterProfile) && reportData?.holterProfile?.version === 1;

  const shouldRenderPingAiApiSection =
    profileId && aiProcessing && !state.isOpenNoApiCallingModal && !state.isExpiredToken;

  // Display the reset study screen while resetting

  return {
    params,
    studyFid,
    profileId,
    meRedux,
    loading,
    loadingPageAction,
    isMacOS,
    defaultActiveTab,
    state,
    setState,
    isNotEdit,
    setIsNotEdit,
    isLoadIndexDB,
    setIsLoadIndexDB,
    isLoadBeatData,
    setIsLoadBeatData,
    isWsConnected,
    setIsWsConnected,
    isResetingStudy,
    setIsResetingStudy,
    isOpenWrongSomething,
    setIsOpenWrongSomeThing,
    typeWrong,
    setTypeWrong,
    reportData,
    setReportData,
    reportInfo,
    setReportInfo,
    aiProcessing,
    setAiProcessing,
    getReportInfo,
    ecgDataMap,
    setEcgDataMap,
    isStartedAiProcessing,
    setIsStartedAiProcessing,
    isOpenProcessingLimit,
    setIsOpenProcessingLimit,
    limitProcessingStudy,
    setLimitProcessingStudyState,
    setOriginalDailySummaries,
    setUpdatedDailySummaries,
    setFindings,
    setFacilityNote,
    setProfileAfibAvgHr,
    isShowTabHeader,
    setCombinedEventsInHeatmap,
    setEcgBookmarks,
    setActionSnapshot,
    setHolterBeatEventsCount,
    setHolterRhythmEventsCount,
    setIsRenderViewerEcg,
    isLoadedIndexDB,
    isUndoDisabled,
    setIsUndoDisabled,
    isRedoDisabled,
    setIsRedoDisabled,
    isDisabledBtn,
    setIsDisabledBtn,
    checkInitialUserRoom,
    setCheckInitialUserRoom,
    listDownloadFile,
    setListDownloadFile,
    setIsDownloadingBeatFile,
    isDownloadingPdfReport,
    setInitTemplateOverview,
    migrateAiData,
    setMigrateAiData,
    checkInitialUserRoomRef,
    isInitialMount,
    isInitialSessionInfo,
    startTime,
    endTime,
    checkExpiredTokenInterval,
    isGeneratingReport,
    studyId,
    facilityId,
    reportId,
    isAbortedStudy,
    startReport,
    timezoneOffset,
    roomReport,
    updatingTabCount,
    tabIndexConfirmNavigate,
    beatsRef,
    ecgEventsRef,
    stripsRef,
    pdfReportRef,
    hrSummaryRef,
    findingRef,
    ecgDisclosureRef,
    sessionInfoRef,
    timerRenderReport,
    indexedDbInfoRef,
    checkBeatsTimeOut,
    isPreventChangeTabWhenSaving,
    BEAT_RETRY_LIMIT,
    BEAT_RETRY_DELAY,
    fetchEventsCount,
    getDatabaseState,
    clearDataAndResaveSessionInfo,
    handleClearIndexDB,
    onDiscardChangeSavedData,
    onKeepEditingWithSavedData,
    resetState,
    updateDataEcgDataInMetadata,
    updateAndProcessBeatsInWorker,
    handleUpdateBeats,
    handleReloadBeat,
    handleReloadSummary,
    fetchReportData,
    toggleDisplayStudyInfoDrawer,
    toggleDisplayPatientInfoDrawer,
    toggleDisplayFacilityInfoDrawer,
    onClickReload,
    onClickGoCallCenter,
    handleOnClickArtifactReport,
    navigateTab,
    onClickCancelNavigateTab,
    onClickNavigateWithoutSave,
    onClickConfirmNavigateTab,
    saveReportOnClick,
    handleNoActivity,
    onClickAcceptLimitProcessingStudy,
    checkDataChange,
    handleMigrateBeatFinalPathOn403,
    onClickConfirmMigrate403,
    checkTotalBeatMatchInIndexDB,
    checkAvailableStorageSpace,
    downloadAndLogTime,
    handleInitialBeatDataProcessing,
    isBeatDataIncomplete,
    verifyAndRefreshEcgBeatData,
    logAiBugFinalBeat,
    handleCheckExpiredTokenInterval,
    handleRemoveCheckExpiredTokenInterval,
    handledDeleteUnusedDatabases,
    handleFetchBookmarks,
    migrateAiDataVer1ToVer2,
    displayNoApiCallingModal,
    handleConfirmActivity,
    displayModalIndexDBError,
    limitUserProcessingContent,
    onClickConfirmNavigateV2Tab,
    isOldVersion,
    shouldRenderPingAiApiSection,
  };
};

export default useAiReportPage;
